import { computed, isRef } from 'vue';

/**
 * useCambioButacaDispatcher — Decisor de camino UI para el flujo de
 * "Cambiar asiento desde perfil".
 *
 * Replica el dispatcher canónico de
 * `src/components/frontend/abonos/nuevo/ProcesoCompraAbonadoNormal.vue:70-83`:
 *  - `recinto.tipo_proceso_online === 1` → `regiones` (zonas planas + SelectorAsientosComponent / LeafletComponent)
 *  - `recinto.tipo_proceso_online === 2` → `secciones` (SeccionesComponent legacy)
 *  - `recinto.tipo_proceso_online === 3` → `mapa-general` (LeafletComponent sin paso de zona)
 *  - `null | undefined`                  → `fallback-legacy` (mantiene comportamiento previo dependiente de `tipo_svg`)
 *
 * `tipo_svg` queda como pista de layout visual DENTRO de un camino
 * (regiones usa zonas planas; mapa-general usa Leaflet 3D), nunca como
 * selector operativo del camino. Esto corrige el bug "secciones/svg no
 * encontrado" reportado para `tipo_svg=1 + tipo_proceso_online=1`.
 *
 * API:
 *  - `getCamino(tipo_proceso_online)` — función pura, testable sin Vue.
 *  - `camino` — `ComputedRef<Camino>` reactivo; acepta Ref/function/value.
 *
 * @param {import('vue').Ref<number|null>|number|null|function} tipoProcesoOnlineSource
 *        `recinto.tipo_proceso_online` (ref, getter o valor directo)
 * @returns {{
 *   camino: import('vue').ComputedRef<Camino>,
 *   getCamino: (tipoProcesoOnline: number|null|undefined) => Camino,
 * }}
 */
export const CAMINOS = Object.freeze({
    REGIONES:        'regiones',
    SECCIONES:       'secciones',
    MAPA_GENERAL:    'mapa-general',
    FALLBACK_LEGACY: 'fallback-legacy',
});

/**
 * Decisor puro del camino UI a partir del `tipo_proceso_online` del recinto.
 *
 * Función pura, sin reactividad, sin side-effects. Testable directamente.
 *
 * Acepta tanto números (1, 2, 3) como strings numéricos ('1', '2', '3')
 * que provienen del backend, normalizándolos con `Number()` y validando
 * que el resultado sea un entero en el rango [1, 3]. Valores no
 * normalizables (null, undefined, NaN, out-of-range, no enteros, etc.)
 * caen al `FALLBACK_LEGACY` para preservar el comportamiento previo.
 *
 * @param {number|string|null|undefined} tipoProcesoOnline
 * @returns {'regiones'|'secciones'|'mapa-general'|'fallback-legacy'}
 */
export function getCamino(tipoProcesoOnline) {
    const n = Number(tipoProcesoOnline);
    if (!Number.isInteger(n) || n < 1 || n > 3) return CAMINOS.FALLBACK_LEGACY;
    if (n === 1) return CAMINOS.REGIONES;
    if (n === 2) return CAMINOS.SECCIONES;
    return CAMINOS.MAPA_GENERAL;
}

/**
 * Hook composable que expone el camino reactivo en función del
 * `tipo_proceso_online` del recinto.
 *
 * @param {import('vue').Ref<number|null>|number|null|function} tipoProcesoOnlineSource
 *        `recinto.tipo_proceso_online` (acepta ref, getter o valor)
 */
export function useCambioButacaDispatcher(tipoProcesoOnlineSource) {

    /**
     * Resuelve el `tipo_proceso_online` desde la fuente inyectada.
     * @returns {number|null|undefined}
     */
    const resolve = () => {
        if (isRef(tipoProcesoOnlineSource)) return tipoProcesoOnlineSource.value;
        if (typeof tipoProcesoOnlineSource === 'function') return tipoProcesoOnlineSource();
        return tipoProcesoOnlineSource;
    };

    /**
     * Camino UI reactivo. Se recalcula cuando la fuente ref/unref cambia.
     * @type {import('vue').ComputedRef<'regiones'|'secciones'|'mapa-general'|'fallback-legacy'>}
     */
    const camino = computed(() => getCamino(resolve()));

    return {
        camino,
        getCamino,
        CAMINOS,
    };
}

export default useCambioButacaDispatcher;
